import { useQueryClient } from '@tanstack/react-query';
import {
  agentSessionsQueryKey,
  archiveAgentSession,
  getAgentSession,
  getAgentSessions,
  unarchiveAgentSession,
} from 'api/agent';
import { compact, filter, first, isArray, isEmpty, orderBy, some } from 'es-toolkit/compat';
import { AgentSession } from 'interfaces/agentSession';
import React from 'react';

import { useOpenStudyReportRoute } from 'components/Copilot/hooks/useOpenStudyReportRoute';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';
import { CopilotDrawerTabValue } from '../CopilotDrawerHeader';

type UseCopilotSessionManagementParams = {
  activeSessionId: string | null;
  copilotDrawerOpen: boolean;
  setFallbackSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveTab: (value: CopilotDrawerTabValue) => void;
  clearSessionViewState?: (sessionId: string | null | undefined) => void;
  stopStreamingSession?: (sessionId: string | null | undefined, options?: { restorePrompt?: boolean }) => void;
};

const sortStudyIds = (studyIds?: string[] | null) => [...compact(studyIds || [])].sort();

const getSessionStudyIds = (session: AgentSession) =>
  compact(
    !isEmpty(session.sessionData?.reportState?.studyIds) ? session.sessionData.reportState.studyIds : session.studyIds
  );

const getConversationMessages = (session?: AgentSession | null) => session?.sessionData?.conversation?.messages || [];

const shouldPreserveCachedSession = ({
  cachedSession,
  fetchedSession,
}: {
  cachedSession: AgentSession | null;
  fetchedSession: AgentSession;
}) => {
  if (!cachedSession) {
    return false;
  }

  const cachedMessages = getConversationMessages(cachedSession);
  const fetchedMessages = getConversationMessages(fetchedSession);

  if (fetchedMessages.length >= cachedMessages.length) {
    return false;
  }

  const fetchedMatchesCachedPrefix = fetchedMessages.every((fetchedMessage, index) => {
    const cachedMessage = cachedMessages[index];

    return Boolean(
      cachedMessage &&
        cachedMessage.id === fetchedMessage.id &&
        cachedMessage.role === fetchedMessage.role &&
        cachedMessage.content === fetchedMessage.content &&
        cachedMessage.status === fetchedMessage.status
    );
  });

  if (!fetchedMatchesCachedPrefix) {
    return false;
  }

  const cachedTail = cachedMessages.slice(fetchedMessages.length);

  return cachedTail.some(
    (message) => message.role === 'assistant' && (message.status === 'failed' || message.status === 'streaming')
  );
};

const getSessionScopeMismatchState = ({
  session,
  currentLabId,
  currentStudyIds,
}: {
  session: AgentSession;
  currentLabId: string | null;
  currentStudyIds: string[];
}) => {
  const sessionLabId = session.sessionData?.reportState?.filters?.labId || null;
  const sessionStudyIds = sortStudyIds(session.sessionData?.reportState?.studyIds || session.studyIds || []);
  const normalizedCurrentStudyIds = sortStudyIds(currentStudyIds);
  const hasLabMismatch = Boolean(currentLabId && sessionLabId && currentLabId !== sessionLabId);
  const hasStudyMismatch = normalizedCurrentStudyIds.join('|') !== sessionStudyIds.join('|');

  return {
    sessionLabId,
    sessionStudyIds,
    hasLabMismatch,
    hasStudyMismatch,
    hasScopeMismatch: hasLabMismatch || hasStudyMismatch,
  };
};

const getSessionScopeSwitchPromptText = ({
  hasLabMismatch,
  hasStudyMismatch,
  hasKnownSessionLabId,
}: {
  hasLabMismatch: boolean;
  hasStudyMismatch: boolean;
  hasKnownSessionLabId: boolean;
}) => {
  if (!hasKnownSessionLabId && hasStudyMismatch) {
    return 'This session was saved in a different scope. Copilot may need to switch the current page to that lab and study scope before reopening it.';
  }

  if (hasLabMismatch && hasStudyMismatch) {
    return 'This session was saved in a different lab and study scope. Copilot needs to switch the current page to that lab and study scope before reopening it.';
  }

  if (hasLabMismatch) {
    return 'This session was saved in a different lab. Copilot needs to switch the current page to that lab before reopening it.';
  }

  return 'This session was saved in a different study scope. Copilot needs to switch the current page to that study scope before reopening it.';
};

// Owns session list/query state and archive/unarchive optimistic mutations.
export const useCopilotSessionManagement = ({
  activeSessionId,
  copilotDrawerOpen,
  setFallbackSessionId,
  setActiveTab,
  clearSessionViewState = () => undefined,
  stopStreamingSession = () => undefined,
}: UseCopilotSessionManagementParams) => {
  // These are read from shared app hooks so caller only supplies local UI state setters.
  const queryClient = useQueryClient();
  const openStudyReportRoute = useOpenStudyReportRoute();
  const confirmWithModal = useConfirmation();
  const { labId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const rawStudyId = queryParams.filters?.studyId;
  const selectedStudyIds = compact(isArray(rawStudyId) ? rawStudyId : rawStudyId ? [rawStudyId] : []);
  const selectedStudyId = first(selectedStudyIds);
  const [archiveMutationSessionIds, setArchiveMutationSessionIds] = React.useState<string[]>([]);
  const [selectionLoadingSessionId, setSelectionLoadingSessionId] = React.useState<string | null>(null);

  const resolveSessionForSelection = React.useCallback(
    async (session: AgentSession) => {
      const cachedSession = queryClient.getQueryData<AgentSession>(['agentSession', session.id]);
      const resolvedSession = cachedSession || session;

      if (resolvedSession.sessionData?.reportState?.filters?.labId) {
        return resolvedSession;
      }

      return queryClient.fetchQuery(['agentSession', session.id], () => getAgentSession(session.id));
    },
    [queryClient]
  );

  const allSessionsQuery = useQueryWithErrorAndRetrySnackbar(
    agentSessionsQueryKey,
    () => getAgentSessions('includeArchived=true&summary=true'),
    {
      enabled: copilotDrawerOpen,
      queriedEntityName: 'copilot sessions',
    }
  );

  const sessionsQuery = allSessionsQuery;
  const archivedSessionsQuery = allSessionsQuery;

  const activeSessions = React.useMemo(
    () => filter(allSessionsQuery.data || [], (session) => !session.archivedAt),
    [allSessionsQuery.data]
  );
  const archivedSessions = React.useMemo(
    () => filter(allSessionsQuery.data || [], (session) => Boolean(session.archivedAt)),
    [allSessionsQuery.data]
  );

  const activeSessionQuery = useQueryWithErrorAndRetrySnackbar(
    ['agentSession', activeSessionId],
    async () => {
      const sessionId = activeSessionId as string;
      const cachedSession = queryClient.getQueryData<AgentSession>(['agentSession', sessionId]) || null;
      const fetchedSession = await getAgentSession(sessionId);

      return shouldPreserveCachedSession({ cachedSession, fetchedSession })
        ? cachedSession || fetchedSession
        : fetchedSession;
    },
    {
      enabled: Boolean(activeSessionId && copilotDrawerOpen),
      // Keep optimistic cache fresh for 10s so that clicking a session
      // doesn't immediately refetch what we just wrote to the cache.
      staleTime: 10_000,
      queriedEntityName: 'copilot session',
    }
  );

  const openSelectedSession = React.useCallback(
    ({
      session,
      sessionLabId,
      sessionStudyIds,
    }: {
      session: AgentSession;
      sessionLabId: string | null;
      sessionStudyIds: string[];
    }) => {
      // Only seed the active-session cache when the session has messages.
      // Summary-mode sessions (messages: []) would show an empty chat until the
      // staleTime-delayed refetch completes — so skip caching them and let the
      // active-session query fetch immediately after navigation.
      const hasMessages = !isEmpty(session.sessionData?.conversation?.messages);
      if (hasMessages) {
        queryClient.setQueryData(['agentSession', session.id], session);
      }
      setFallbackSessionId(session.id);
      setActiveTab('chat');
      openStudyReportRoute({
        keepDrawerOpen: true,
        sessionId: session.id,
        studyIds: sessionStudyIds,
        activeStudyId: session.sessionData.reportState.activeStudyId || sessionStudyIds[0] || selectedStudyId || null,
        labId: sessionLabId || labId,
      });
    },
    [labId, openStudyReportRoute, queryClient, selectedStudyId, setActiveTab, setFallbackSessionId]
  );

  const confirmSessionScopeSwitch = React.useCallback(
    async ({
      session,
      mismatchState,
    }: {
      session: AgentSession;
      mismatchState: ReturnType<typeof getSessionScopeMismatchState>;
    }) => {
      if (!mismatchState.hasScopeMismatch) {
        return true;
      }

      return confirmWithModal({
        title: `Open ${session.title || 'saved session'} in its saved context?`,
        text: getSessionScopeSwitchPromptText({
          hasLabMismatch: mismatchState.hasLabMismatch,
          hasStudyMismatch: mismatchState.hasStudyMismatch,
          hasKnownSessionLabId: Boolean(mismatchState.sessionLabId),
        }),
        confirmButtonProps: { title: 'Switch and open' },
        cancelButtonProps: { title: 'Cancel' },
      });
    },
    [confirmWithModal]
  );

  const handleSelectSession = async (session: AgentSession) => {
    const cachedSession = queryClient.getQueryData<AgentSession>(['agentSession', session.id]);
    const initiallyResolvedSession = cachedSession || session;

    const initialMismatchState = getSessionScopeMismatchState({
      session: initiallyResolvedSession,
      currentLabId: labId,
      currentStudyIds: selectedStudyIds,
    });

    // Same scope with known lab → open immediately, no server round-trip needed.
    if (!initialMismatchState.hasScopeMismatch) {
      const hasKnownLabScope = Boolean(initialMismatchState.sessionLabId || !labId);

      if (hasKnownLabScope) {
        openSelectedSession({
          session: initiallyResolvedSession,
          sessionLabId: initialMismatchState.sessionLabId,
          sessionStudyIds: getSessionStudyIds(initiallyResolvedSession),
        });
        return;
      }

      // Lab scope unknown from summary — need to fetch full session details.
      setActiveTab('chat');
      setSelectionLoadingSessionId(session.id);

      let resolvedSession: AgentSession;

      try {
        resolvedSession = await resolveSessionForSelection(initiallyResolvedSession);
      } catch (error) {
        setActiveTab('sessions');
        throw error;
      } finally {
        setSelectionLoadingSessionId((currentSessionId) => (currentSessionId === session.id ? null : currentSessionId));
      }

      const resolvedMismatchState = getSessionScopeMismatchState({
        session: resolvedSession,
        currentLabId: labId,
        currentStudyIds: selectedStudyIds,
      });

      if (resolvedMismatchState.hasScopeMismatch) {
        const confirmedScopeSwitch = await confirmSessionScopeSwitch({
          session: resolvedSession,
          mismatchState: resolvedMismatchState,
        });

        if (!confirmedScopeSwitch) {
          setActiveTab('sessions');
          return;
        }
      }

      openSelectedSession({
        session: resolvedSession,
        sessionLabId: resolvedMismatchState.sessionLabId,
        sessionStudyIds: getSessionStudyIds(resolvedSession),
      });
      return;
    }

    // Scope mismatch detected — ask user before switching.
    const confirmedInitialScopeSwitch = await confirmSessionScopeSwitch({
      session: initiallyResolvedSession,
      mismatchState: initialMismatchState,
    });

    if (!confirmedInitialScopeSwitch) {
      return;
    }

    // When the summary already has lab scope, open immediately — the active-session
    // query will fetch full details in the background after navigation.
    if (initialMismatchState.sessionLabId) {
      openSelectedSession({
        session: initiallyResolvedSession,
        sessionLabId: initialMismatchState.sessionLabId,
        sessionStudyIds: getSessionStudyIds(initiallyResolvedSession),
      });
      return;
    }

    // Lab scope unknown from summary — resolve full session before navigating.
    const resolvedSession = await resolveSessionForSelection(initiallyResolvedSession);
    const { sessionLabId } = getSessionScopeMismatchState({
      session: resolvedSession,
      currentLabId: labId,
      currentStudyIds: selectedStudyIds,
    });

    openSelectedSession({
      session: resolvedSession,
      sessionLabId,
      sessionStudyIds: getSessionStudyIds(resolvedSession),
    });
  };

  const mutateSessionArchiveState = async ({ session, archived }: { session: AgentSession; archived: boolean }) => {
    if (archiveMutationSessionIds.includes(session.id)) {
      return;
    }

    const mutationTimestamp = new Date().toISOString();
    const optimisticSession: AgentSession = {
      ...session,
      archivedAt: archived ? mutationTimestamp : null,
      updatedAt: mutationTimestamp,
    };
    const previousSessions = queryClient.getQueryData<AgentSession[]>(agentSessionsQueryKey);
    const previousSession = queryClient.getQueryData<AgentSession>(['agentSession', session.id]) || session;
    const wasActiveSession = activeSessionId === session.id;

    setArchiveMutationSessionIds((currentIds) => [...currentIds, session.id]);
    await Promise.all([
      queryClient.cancelQueries(agentSessionsQueryKey),
      queryClient.cancelQueries(['agentSession', session.id]),
    ]);

    queryClient.setQueryData(agentSessionsQueryKey, (existingSessions: AgentSession[] | undefined) => {
      if (!existingSessions) {
        return existingSessions;
      }

      return existingSessions.map((existingSession) =>
        existingSession.id === session.id ? optimisticSession : existingSession
      );
    });

    // If user archives the currently open session, clear chat state and route context immediately.
    if (archived && wasActiveSession) {
      stopStreamingSession(session.id, { restorePrompt: false });
      clearSessionViewState(session.id);
      setFallbackSessionId(null);
      openStudyReportRoute({
        keepDrawerOpen: true,
        studyIds: session.studyIds,
        activeStudyId: session.sessionData.reportState.activeStudyId || session.studyIds[0] || selectedStudyId || null,
      });
    } else if (archived) {
      stopStreamingSession(session.id, { restorePrompt: false });
      clearSessionViewState(session.id);
    }

    try {
      const updatedSession = archived ? await archiveAgentSession(session.id) : await unarchiveAgentSession(session.id);

      queryClient.setQueryData(agentSessionsQueryKey, (existingSessions: AgentSession[] | undefined) => {
        if (!existingSessions) {
          return existingSessions;
        }

        return existingSessions.map((existingSession) =>
          existingSession.id === session.id ? updatedSession : existingSession
        );
      });

      if (archived) {
        queryClient.removeQueries(['agentSession', session.id]);
      } else {
        queryClient.setQueryData(['agentSession', session.id], updatedSession);
      }
    } catch (error) {
      // Revert optimistic cache/UI state on failure to keep list + chat in sync.
      queryClient.setQueryData(agentSessionsQueryKey, previousSessions || []);
      queryClient.setQueryData(['agentSession', session.id], previousSession);

      if (archived && wasActiveSession) {
        setFallbackSessionId(previousSession.id);
        openStudyReportRoute({
          keepDrawerOpen: true,
          sessionId: previousSession.id,
          studyIds: previousSession.studyIds,
          activeStudyId:
            previousSession.sessionData.reportState.activeStudyId ||
            previousSession.studyIds[0] ||
            selectedStudyId ||
            null,
        });
      }

      throw error;
    } finally {
      setArchiveMutationSessionIds((currentIds) => currentIds.filter((currentId) => currentId !== session.id));
      await queryClient.invalidateQueries(agentSessionsQueryKey);
    }
  };

  const handleArchiveSession = async (session: AgentSession) => {
    await mutateSessionArchiveState({ session, archived: true });
  };

  const handleUnarchiveSession = async (session: AgentSession) => {
    await mutateSessionArchiveState({ session, archived: false });
  };

  // Find the most recently updated active session that matches the current lab+study context
  // and is not the session already loaded, so users can quickly resume prior work.
  const suggestedSession = React.useMemo(() => {
    if (!labId || activeSessions.length === 0) return null;

    const candidates = activeSessions.filter((session) => {
      if (session.id === activeSessionId) return false;
      const sessionLabId = session.sessionData?.reportState?.filters?.labId;
      if (sessionLabId !== labId) return false;
      // If the user has studies selected, require at least one overlap.
      if (selectedStudyIds.length > 0) {
        const hasOverlap = some(selectedStudyIds, (sid) => session.studyIds?.includes(sid));
        if (!hasOverlap) return false;
      }
      return true;
    });

    if (candidates.length === 0) return null;

    return first(orderBy(candidates, [(s) => s.updatedAt || s.createdAt || ''], ['desc'])) || null;
  }, [activeSessions, activeSessionId, labId, selectedStudyIds]);

  return {
    sessionsQuery,
    archivedSessionsQuery,
    activeSessions,
    archivedSessions,
    activeSessionQuery,
    archiveMutationSessionIds,
    selectionLoadingSessionId,
    suggestedSession,
    handleSelectSession,
    handleArchiveSession,
    handleUnarchiveSession,
  };
};
